import { createContext } from 'preact'
import { useState, useContext, useMemo, useCallback, useEffect, useRef } from 'preact/hooks'
import { useAthosContext } from 'libs/athos/fetchers/provider'
import { useOption } from 'libs/athos/hooks/useOption'
import { SortProvider } from 'libs/athos/hooks/useSort'
import { RANGE_INDICATOR } from '../components/collection/filters/FilterUtils'

const FiltersContext = createContext()

const FILTERS_BLACKLIST = []

function parseFiltersFromResults(results) {
  if (!results?.facets) return []
  return results.facets.filter((facet) => {
    return !FILTERS_BLACKLIST.some((field) => field === facet.field)
  })
}

export function FiltersProvider({ children }) {
  const { results, refine, isLoading } = useAthosContext()
  const [isOpen, setIsOpen] = useState(true)

  // Computed once, synchronously, on the very first render — so the drawer's
  // <x-modal> can be marked visible from its first paint instead of mounting
  // closed and toggling open in an effect (which would animate the open
  // transition on load). No resize listener: a shopper who closes it manually
  // isn't forced back in, and resizing mid-session doesn't override their choice.
  const [isDesktopDefaultOpen] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches
  )

  const activeFilters = useOption('filters', {
    defaultValue: new Set(),
    syncWithURL: true
  })

  const fetchedFilters = useMemo(() => parseFiltersFromResults(results), [results])

  // Handles persisting initial filter values and just disabling them
  const [initialFilters, setInitialFilters] = useState(fetchedFilters)

  // Populates the filter list the first time real facet data arrives, regardless
  // of whether/when the drawer was opened. Needed because the desktop auto-open
  // (below) fires before the first fetch resolves, and initialFilters would
  // otherwise freeze at [] forever.
  useEffect(() => {
    if (!isLoading && initialFilters.length === 0 && fetchedFilters.length > 0) {
      setInitialFilters(fetchedFilters)
    }
  }, [isLoading, fetchedFilters])

  // Handles active filter state and 'cancel' function
  const [restorableFilters, setRestorableFilters] = useState(new Set())

  // Improved checkFilter that returns either the filter or the active filter that needs to be modified
  const checkFilter = useCallback((filter) => {
    if (!filter.includes(RANGE_INDICATOR)) {
      return { filter, existingFilter: null }
    }

    const filterPrefix = filter.split(':')[0]

    for (const activeFilter of activeFilters) {
      if (activeFilter.includes(filterPrefix)) {
        return { filter, existingFilter: activeFilter }
      }
    }

    return { filter, existingFilter: null }
  }, [activeFilters])

  const handleToggleFilter = useCallback((filter) => {
    const filters = new Set(activeFilters)
    const { filter: checkedFilter, existingFilter } = checkFilter(filter)

    if (existingFilter) {
      // If there's an existing filter with the same prefix, remove it first
      filters.delete(existingFilter)
    }

    if (filters.has(checkedFilter)) {
      filters.delete(checkedFilter)
    } else {
      filters.add(checkedFilter)
    }

    refine('filters', filters)
    refine('page', 1)
  }, [activeFilters, refine, checkFilter])

  const handleRemoveFilter = useCallback((filter) => {
    const filters = new Set(activeFilters)
    const { existingFilter } = checkFilter(filter)

    const filterToRemove = existingFilter || filter

    if (filters.has(filterToRemove)) {
      filters.delete(filterToRemove)
      refine('filters', filters)
      refine('page', 1)
    }
  }, [activeFilters, refine, checkFilter])

  const handleClearFilters = useCallback(() => {
    refine('filters', new Set([]))
    refine('page', 1)
  }, [refine])

  const handleOpenFilters = () => {
    setIsOpen(true)
    setRestorableFilters(activeFilters)
    if (initialFilters.length === 0) setInitialFilters(fetchedFilters)
  }

  const handleCloseFilters = () => {
    setIsOpen(false)
  }

  const handleCancelFilters = () => {
    refine('filters', restorableFilters)
    setRestorableFilters(new Set())
    setIsOpen(false)
  }

  // Single entry point for opening the drawer. Triggers (the toolbar button and
  // the Liquid <filter-toggle> in the collection header) dispatch a `filters.open`
  // event rather than reaching into each other's DOM. A ref keeps the listener
  // registered once while always running the latest closure. (Desktop's default
  // open is handled separately — see isDesktopDefaultOpen above — since it must
  // apply on the very first paint, not via an event after mount.)
  const openFiltersRef = useRef()
  openFiltersRef.current = () => {
    handleOpenFilters()
    document.querySelector('x-modal[data-id="filters"]')?.handleToggle()
  }

  useEffect(() => {
    const handleOpenEvent = () => openFiltersRef.current?.()
    window.addEventListener('filters.open', handleOpenEvent)
    return () => window.removeEventListener('filters.open', handleOpenEvent)
  }, [])

  // Desktop's <x-modal> is already rendered visible (via isDesktopDefaultOpen,
  // see FilterDrawer) — this just syncs the Preact-side bookkeeping
  // (restorableFilters/initialFilters) to match, without touching the DOM.
  useEffect(() => {
    if (isDesktopDefaultOpen) {
      handleOpenFilters()
    }
  }, [])

  const value = {
    isOpen,
    isLoading,
    isDesktopDefaultOpen,
    activeFilters,
    fetchedFilters,
    initialFilters,
    restorableFilters,
    handleToggleFilter,
    handleRemoveFilter,
    handleClearFilters,
    handleOpenFilters,
    handleCloseFilters,
    handleCancelFilters
  }

  return <FiltersContext.Provider value={value}><SortProvider>{children}</SortProvider></FiltersContext.Provider>
}

export function useFilters() {
  const context = useContext(FiltersContext)
  if (!context) {
    throw new Error('useFilters must be used within a FiltersProvider')
  }
  return context
}
